/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Button, CircularProgress, Typography, useMediaQuery } from "@mui/material";
import { useTranslation } from "react-i18next";
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined';
import { useEffect, useState } from "react";
import CustomInput from "../../inputs/customInput";
import BranchService from "../../../services/branchService";
import StreetDropdown from "../../dropdowns/streetDropdown";
import { useTheme } from "@emotion/react";
import CityDropdown from "../../dropdowns/cityDropdown";

const UpdateBranchBox = ({
    handleReload,
    setPopupMsg,
    setPopupType,
    setShowPopup,
    branchId }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    const [branch, setBranch] = useState({});
    const [selectedStreet, setSelectedStreet] = useState(null);
    const [selectedCity, setSelectedCity] = useState(null);
    const [updateErrors, setUpdateErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    const [formValues, setFormValues] = useState({
        name: branch.name,
        note: branch.note,
        streetId: branch.street && branch.street.id
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormValues((prevValues) => ({
            ...prevValues,
            [name]: value,
        }));
    };


    const fetchBranch = async () => {
        try {
            const data = await BranchService.fetchOne(branchId);
            setBranch(data);
        } catch (error) {
            console.error('Error fetching branch:', error);
        }
    };



    useEffect(() => {
        fetchBranch();
    }, [branchId]);


    useEffect(() => {
        if (branch) {
            setFormValues({
                name: branch.name || '',
                note: branch.note || '',
                streetId: branch.street?.id || null,
            });
            setSelectedStreet(branch.street || null);
            setSelectedCity(branch.street ? branch.street.city : null);
            setUpdateErrors({});
        }
    }, [branch]);


    const validateForm = () => {
        const newUpdateErrors = {};

        setUpdateErrors(newUpdateErrors);

        return Object.keys(newUpdateErrors).length === 0;
    };


    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isLoading) return;

        if (validateForm()) {
            try {
                setIsLoading(true);
                const data = await BranchService.updateBranch({
                    id: branch.id,
                    name: formValues.name,
                    note: formValues.note,
                    streetId: formValues.streetId
                });
                if (data.status === 200) {
                    setPopupMsg(data.data.message);
                    setPopupType('update');
                    setShowPopup(true);
                    handleReload();
                    setFormValues({ name: '' });
                }

                setUpdateErrors({});
            } catch (error) {
                if (error.response?.status === 400) {
                    const responseErrors = error.response.data || {};
                    const formattedErrors = {};

                    Object.keys(responseErrors).forEach((field) => {
                        formattedErrors[field] = responseErrors[field];
                    });

                    setUpdateErrors(formattedErrors);
                } else {
                    setPopupMsg(error.response.data.error);
                    setPopupType('error');
                    console.error("An unexpected error occurred:", error);
                    setUpdateErrors({ general: error.response.data.error });
                }
            } finally {
                setIsLoading(false);
            }
        }
    };



    return (
        <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
                width: '100%',
                bgcolor: 'background.paper',
                borderRadius: 2,
                border: 1,
                borderColor: theme.palette.customGrey.borderInput,
                p: isTablet ? 2 : 4,
            }}
        >
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between'
            }}>
                <Typography id="transition-modal-title" variant="h6" component="h2">
                    {t('updateBranch')}
                </Typography>
            </Box>


            <Box>
                <CityDropdown
                    helperText={updateErrors.city}
                    error={!!updateErrors.city}
                    setFormValues={setFormValues}
                    isFilter={false}
                    selectedCity={selectedCity}
                    setSelectedCity={setSelectedCity}
                />

                <StreetDropdown
                    helperText={updateErrors.street}
                    error={!!updateErrors.street}
                    setFormValues={setFormValues}
                    isFilter={false}
                    selectedStreet={selectedStreet || null}
                    setSelectedStreet={setSelectedStreet}
                    cityId={selectedCity ? selectedCity.id : null}
                />


                <CustomInput
                    label={t('name')}
                    placeholder={t('nameHere')}
                    name="name"
                    type='text'
                    value={formValues.name || ''}
                    error={!!updateErrors.name}
                    helperText={updateErrors.name}
                    onChange={handleChange}
                />

                <CustomInput
                    label={t('note')}
                    placeholder={t('noteHere')}
                    name="note"
                    type='text'
                    value={formValues.note || ''}
                    onChange={handleChange}
                />

                {updateErrors.general && (
                    <Typography align="center" color="error" variant="body2" sx={{ mt: 2 }}>
                        {updateErrors.general}
                    </Typography>
                )}

                <Box sx={{
                    display: 'flex', justifyContent: 'center', alignItems: 'center',
                    mt: updateErrors.general ? 2 : 4
                }}>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        sx={{ textTransform: 'none', width: 140, height: 40 }}
                    >
                        {isLoading ? <CircularProgress color="secondary" size={28} /> :
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <p>{t('update')}</p>
                                <DriveFileRenameOutlineOutlinedIcon fontSize="small" sx={{ mx: 0.4 }} />
                            </Box>
                        }
                    </Button>
                </Box>
            </Box>


        </Box>
    );
}

export default UpdateBranchBox;
